import type { Config } from 'effect';

import { Effect, Option } from 'effect';

import { getBrowserResourceEndpoint } from '../config/api';
import { getGcsPublicUrl } from '../config/image';

import { RuntimeClientConfig } from '../runtime/RuntimeClientConfig';
import { BACKEND_RESOLVE_PATH_PREFIX } from './imageOrigins';

const REGEX_META = /[.*+?^${}()|[\]\\]/g;

// Parse a CDN-cache URL into its `(filter, rel)` segments. Pure (no Config),
// so it can be unit-tested without an Effect runtime.
//
//   `${cdnPrefix}/cache/{filter}/{rel}`  →  Some({ filter, rel })
//
// Returns `None` for URLs that don't belong to the CDN, and URLs missing a
// filter or relative-path segment.
export function parseCdnImageUrl(
  src: string,
  cdnPrefix: string
): Option.Option<{ filter: string; rel: string }> {
  const escaped = cdnPrefix.replace(REGEX_META, '\\$&');
  const match = new RegExp(`^${escaped}/cache/(?<filter>[^/]+)/(?<rel>.+)$`).exec(src);
  if (!match?.groups) return Option.none();
  const { filter, rel } = match.groups;
  if (!filter || !rel) return Option.none();
  return Option.some({ filter, rel });
}

// Reverse-map a CDN URL into the backend `resolve` URL — the inverse of
// `resolveImageUrl`'s CDN branch.
//
//   CDN     `${gcsPublicUrl}/cache/{filter}/{rel}`
//   Backend `${apiEndpoint}/media/cache/resolve/{filter}/{rel}`
//
// Returns `None` when no fallback is applicable: CDN env unset (local dev), or
// `src` doesn't match the CDN cache pattern (external images including YouTube
// thumbnails). The source is a URL string: a statically imported image has no
// backend variant, so the rendering shell never asks for its fallback.
export function getImageBackendFallback(
  src: string
): Effect.Effect<Option.Option<string>, Config.ConfigError> {
  return Effect.gen(function* () {
    const cdnPrefix = yield* getGcsPublicUrl;
    const apiEndpoint = yield* getBrowserResourceEndpoint;
    return Option.flatMap(cdnPrefix, (prefix) =>
      Option.map(
        parseCdnImageUrl(src, prefix),
        ({ filter, rel }) => `${apiEndpoint}${BACKEND_RESOLVE_PATH_PREFIX}/${filter}/${rel}`
      )
    );
  });
}

// Sync wrapper for render-time call sites. Reads config through
// `RuntimeClientConfig`, so the shell must have installed its engine platform.
// A config error means there is no fallback.
export function getImageBackendFallbackOptionSync(src: string): Option.Option<string> {
  return RuntimeClientConfig.runSync(
    getImageBackendFallback(src).pipe(Effect.orElseSucceed(() => Option.none<string>()))
  );
}
